import { Info, AlertTriangle, Clock } from 'lucide-react';
import type { FeePreview } from '@/features/commission/api';
import type { VariantDraft } from '@/features/product-variant/types';

/**
 * What the customer will pay, shown while the vendor is still typing.
 *
 * The same three numbers the saved listing shows — vendor price, fee, customer price
 * — but computed from the draft, per size, before anything is committed. Without it a
 * vendor picks a price against competitors' shelf prices, saves, and only then learns
 * that shoppers see 10% more than the number they chose.
 *
 * The arithmetic is duplicated here rather than round-tripped per keystroke, and it
 * is the *rate* that comes from the server, so the two cannot disagree about the rule
 * — only about rounding on a number that has not been saved yet.
 */
const rupees = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function fee_for(price: number, fee: FeePreview): number {
  if (!fee.has_fee) return 0;
  return fee.mode === 'FLAT' ? (fee.amount_inr ?? 0) : Math.round(price * ((fee.rate_pct ?? 0) / 100) * 100) / 100;
}

export function FeePreviewPanel({
  fee,
  variants,
  needs_approval = true,
}: {
  fee: FeePreview | undefined;
  variants: VariantDraft[];
  /** False on an edit that goes live immediately (price and stock changes). */
  needs_approval?: boolean;
}) {
  if (!fee) return null;

  const priced = variants
    .map((v, i) => ({ v, i, price: Number(v.price_inr), mrp: v.mrp_inr.trim() === '' ? null : Number(v.mrp_inr) }))
    .filter((r) => Number.isFinite(r.price) && r.price > 0);

  return (
    <div
      className="rounded-card border-2 border-brand-outline-variant/15 bg-secondary/20 p-3"
      data-testid="fee-preview-panel"
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Info className="h-3.5 w-3.5" />
        What customers will pay
      </div>

      {!fee.has_fee ? (
        <p className="text-sm text-muted-foreground" data-testid="fee-preview-none">
          No platform fee on this category — customers pay exactly the price you set.
        </p>
      ) : priced.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="fee-preview-hint">
          {fee.mode === 'FLAT'
            ? `A ${rupees(fee.amount_inr ?? 0)} platform fee is added to your price on this category.`
            : `A ${fee.rate_pct}% platform fee is added to your price on this category.`}{' '}
          Enter a price to see what the customer pays.
        </p>
      ) : (
        <div className="space-y-1.5">
          {priced.map(({ v, i, price, mrp }) => {
            const f = fee_for(price, fee);
            const customer = Math.round((price + f) * 100) / 100;
            const exceeds = mrp != null && customer > mrp;
            return (
              <div key={i} className="text-sm" data-testid={`fee-preview-${i}`}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 tabular-nums">
                  {v.label.trim() && (
                    <span className="text-xs font-medium text-muted-foreground">{v.label.trim()}</span>
                  )}
                  <span>
                    <span className="text-xs text-muted-foreground">You get </span>
                    <span className="font-semibold">{rupees(price)}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">+ fee {rupees(f)}</span>
                  <span className="ml-auto">
                    <span className="text-xs text-muted-foreground">Customer pays </span>
                    <span
                      className="font-heading text-base font-bold text-primary"
                      data-testid={`fee-preview-customer-${i}`}
                    >
                      {rupees(customer)}
                    </span>
                  </span>
                </div>
                {exceeds && (
                  /*
                   * A listing priced above its own printed MRP reads as an error to a
                   * shopper. Better to say so now than to let them save it and wonder
                   * why it stopped selling — their price is fine, the fee on top is not.
                   */
                  <p
                    className="mt-0.5 flex items-start gap-1 text-[11px] font-medium text-destructive"
                    data-testid={`fee-preview-exceeds-mrp-${i}`}
                  >
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    With the fee, customers pay more than the MRP you entered.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {needs_approval && (
        /*
         * The other thing a vendor cannot currently see before saving: this does not
         * go live on tap. Saying it here prevents the "I added it, where is it?"
         * support message that the approval gate otherwise guarantees.
         */
        <p
          className="mt-2 flex items-start gap-1.5 border-t-2 border-brand-outline-variant/10 pt-2 text-xs text-muted-foreground"
          data-testid="fee-preview-approval-note"
        >
          <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          New products are checked by the Pet Market team before customers can see them. Your prices and
          stock are yours to change any time, without another review.
        </p>
      )}
    </div>
  );
}
